// Incident list — active incidents with inline update + resolve actions

import { AlertCircle, CheckCircle2, Clock, MessageSquarePlus } from 'lucide-react'
import { useState } from 'react'
import { StatusBadge } from '@/components/ui/status-badge'
import type { Incident } from '@/lib/types-status'

const INCIDENT_STATUSES = ['investigating', 'identified', 'monitoring', 'resolved'] as const

interface IncidentListProps {
  incidents: Incident[]
  onAddUpdate: (id: number, data: { status: string; message: string }) => void
  onResolve: (id: number) => void
  isUpdating: boolean
}

function timeAgo(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  return `${Math.floor(seconds / 86400)}d ago`
}

export function IncidentList({
  incidents,
  onAddUpdate,
  onResolve,
  isUpdating,
}: IncidentListProps): React.JSX.Element {
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [updateStatus, setUpdateStatus] = useState('investigating')
  const [updateMessage, setUpdateMessage] = useState('')

  function handleSubmitUpdate(incidentId: number): void {
    if (!updateMessage.trim()) return
    onAddUpdate(incidentId, { status: updateStatus, message: updateMessage.trim() })
    setUpdateMessage('')
    setExpandedId(null)
  }

  if (incidents.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-surface-3 p-8 text-center">
        <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-400" />
        <p className="mt-2 text-sm text-text-secondary">No active incidents</p>
        <p className="mt-1 text-xs text-text-tertiary">All systems operating normally</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {incidents.map((incident) => (
        <div
          key={incident.id}
          className="rounded-xl border border-surface-3 bg-surface-0 p-4 shadow-sm"
        >
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
              <div>
                <h4 className="text-sm font-semibold text-text-primary">{incident.title}</h4>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <StatusBadge status={incident.severity} />
                  <StatusBadge status={incident.status} />
                  <span className="flex items-center gap-1 text-xs text-text-tertiary">
                    <Clock className="h-3 w-3" />
                    {timeAgo(incident.startedAt)}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {incident.affectedServices.map((svc) => (
                    <span
                      key={svc}
                      className="rounded bg-surface-2 px-1.5 py-0.5 text-[10px] font-medium text-text-secondary"
                    >
                      {svc}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => setExpandedId(expandedId === incident.id ? null : incident.id)}
                className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-1"
              >
                <MessageSquarePlus className="h-3.5 w-3.5" />
                Update
              </button>
              <button
                type="button"
                onClick={() => onResolve(incident.id)}
                disabled={isUpdating}
                className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Resolve
              </button>
            </div>
          </div>

          {/* Timeline */}
          {incident.updates.length > 0 && (
            <div className="mt-3 border-t border-surface-3 pt-3 space-y-2">
              {incident.updates.slice(0, 3).map((update) => (
                <div key={update.id} className="flex gap-2 text-xs">
                  <StatusBadge status={update.status} />
                  <span className="text-text-primary">{update.message}</span>
                  <span className="shrink-0 text-text-tertiary">{timeAgo(update.createdAt)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Inline update form */}
          {expandedId === incident.id && (
            <div className="mt-3 border-t border-surface-3 pt-3 space-y-3">
              <select
                value={updateStatus}
                onChange={(e) => setUpdateStatus(e.target.value)}
                className="block w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-1.5 text-sm text-text-primary focus:border-admin-500 focus:outline-none"
              >
                {INCIDENT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </select>
              <textarea
                value={updateMessage}
                onChange={(e) => setUpdateMessage(e.target.value)}
                placeholder="What's the latest status?"
                rows={2}
                className="block w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-admin-500 focus:outline-none resize-none"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setExpandedId(null)}
                  className="rounded-lg px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-1"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleSubmitUpdate(incident.id)}
                  disabled={isUpdating || !updateMessage.trim()}
                  className="rounded-lg bg-admin-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-admin-600 disabled:opacity-50"
                >
                  {isUpdating ? 'Posting...' : 'Post Update'}
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
